import { useState, useEffect } from "react";
import MermaidChart from "../MermaidChart";
import "./GTMFlowchartPanel.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Minimum score required to generate a GTM flowchart.
// Below this threshold the idea is "High Risk / Invalid" and GTM is disabled.
const GTM_MIN_SCORE = 300;

/**
 * GTMFlowchartPanel
 *
 * Props:
 *   reportId       {string|number}  Report ID for the API call
 *   initialMermaid {string}         Pre-loaded Mermaid code from DB (optional)
 *   score          {number}         Idea validation score (0–600)
 */
export default function GTMFlowchartPanel({ reportId, initialMermaid = "", score = null }) {
  const [mermaid,   setMermaid]   = useState(initialMermaid);
  const [loading,   setLoading]   = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error,     setError]     = useState("");
  const [generated, setGenerated] = useState(!!initialMermaid);

  // Sync if parent hydrates initialMermaid after mount (e.g. from DB poll)
  useEffect(() => {
    if (initialMermaid && !mermaid) {
      setMermaid(initialMermaid);
      setGenerated(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMermaid]);

  // Score-based lock: locked when score is known, below threshold, and no cached chart
  const isLocked = score !== null && score < GTM_MIN_SCORE && !mermaid;

  const generate = async (force = false) => {
    if (loading || isLocked) return;
    setLoading(true);
    setError("");
    try {
      const url = `${BACKEND}/idea-validation/generate-flowchart/${reportId}${force ? "?force=true" : ""}`;
      const res  = await fetch(url, { method: "POST", credentials: "include" });
      const json = await res.json();
      if (json?.success && json.mermaid) {
        setMermaid(json.mermaid);
        setGenerated(true);
      } else {
        setError(json?.error || "Could not generate the flowchart. Please try again.");
      }
    } catch (err) {
      console.error("Flowchart error:", err);
      setError("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isWorking = loading || rendering;

  return (
    <div className="gtm-panel">
      {/* ── Header ── */}
      <div className="gtm-head">
        <div>
          <span className="gtm-badge">⟿ GTM STRATEGY</span>
          <span className="gtm-sub">AI-generated go-to-market flowchart</span>
        </div>

        {!generated && !loading && (
          <button
            className="gtm-btn"
            onClick={() => generate(false)}
            disabled={isLocked}
            title={isLocked ? "Score too low to unlock GTM strategy" : undefined}
          >
            Generate Flowchart
          </button>
        )}
        {generated && (
          <button
            className="gtm-btn ghost"
            onClick={() => generate(true)}
            disabled={isWorking}
          >
            {isWorking ? "Working…" : "Regenerate"}
          </button>
        )}
      </div>

      {/* ── Low-score notice ── */}
      {isLocked && (
        <div className="gtm-locked-notice">
          <span className="gtm-locked-icon">🔒</span>
          <div>
            <strong>GTM Flowchart Unavailable</strong>
            <p>
              Your idea scored <strong>{score}/600</strong>, which falls below the minimum threshold of{" "}
              <strong>{GTM_MIN_SCORE}</strong> required for a go-to-market strategy.
              Strengthen your idea validation score to unlock this feature.
            </p>
          </div>
        </div>
      )}

      {/* ── Error banner ── */}
      {error && <div className="gtm-error">⚠️ {error}</div>}

      {/* ── Loading shimmer (backend request) ── */}
      {loading && (
        <div className="gtm-loading-wrap">
          <div className="gtm-shimmer-rows">
            <div className="gtm-shimmer-node wide" />
            <div className="gtm-shimmer-arrow" />
            <div className="gtm-shimmer-row">
              <div className="gtm-shimmer-node" />
              <div className="gtm-shimmer-node" />
              <div className="gtm-shimmer-node" />
            </div>
            <div className="gtm-shimmer-arrow" />
            <div className="gtm-shimmer-row">
              <div className="gtm-shimmer-node diamond" />
              <div className="gtm-shimmer-node" />
            </div>
          </div>
          <div className="gtm-loading-label">
            <span className="gtm-pulse-dot" />
            Mapping your GTM strategy with AI…
          </div>
        </div>
      )}

      {/* ── Mermaid chart ── */}
      {mermaid && !loading && (
        <div className={`gtm-chart ${rendering ? "gtm-chart-rendering" : ""}`}>
          {rendering && (
            <div className="gtm-render-overlay">
              <div className="gtm-spin-ring" />
              <span>Rendering diagram…</span>
            </div>
          )}
          <MermaidChart
            code={mermaid}
            onRenderStart={() => setRendering(true)}
            onRenderDone={() => setRendering(false)}
          />
        </div>
      )}

      {/* ── Empty placeholder (score OK, nothing generated yet) ── */}
      {!mermaid && !loading && !error && !isLocked && (
        <div className="gtm-placeholder">
          Click <strong>Generate Flowchart</strong> to map your Awareness → Conversion →
          Referral journey, tailored to your sector and channels.
        </div>
      )}
    </div>
  );
}
